// ESM rewrite of stimulus-validation (github.com/julianrubisch/stimulus-validation)
// The published fork uses Babel-compiled ES5 inheritance incompatible with Rollup 4.
// This file is a direct, faithful port to native ES2022 class syntax.
import { Controller } from "@hotwired/stimulus";
import Validate from "validate.js";

function attributeFromElement(el) {
  const value = el.dataset.attr;
  if (!value) throw new Error("We can't find attribute name. Please add data-attr");
  const name = value.split(".")[1];
  if (!name) throw new Error("We can't find attribute name. Please add data-attr");
  return name;
}

class Attribute {
  constructor(attribute, el, validators) {
    this.attribute = attribute;
    this.el = el;
    this.validators = validators;
  }

  get value() {
    return this.el.type === "checkbox" ? this.el.checked : this.el.value;
  }

  get validationMethods() {
    const result = [];
    Object.entries(this.validators).forEach(([methodName, { attributes }]) => {
      if (attributes.includes(this.attribute)) result.push(methodName);
    });
    return result;
  }
}

class Attributes {
  constructor(controller) {
    this.controller = controller;
    this.dataMap = new Map();
    this.createAttributesMap();
  }

  get(attribute)          { return this.dataMap.get(attribute); }
  has(value)              { return this.dataMap.has(value); }
  forEach(...args)        { return this.dataMap.forEach(...args); }
  refresh()               { this.createAttributesMap(); }

  add(attrName, el, validators = this.validators) {
    const attribute = new Attribute(attrName, el, validators);
    this.dataMap.set(attrName, attribute);
    return attribute;
  }

  createAttributesMap() {
    this.validationElements.forEach(el => this.add(attributeFromElement(el), el));
  }

  get size()               { return this.dataMap.size; }
  get identifier()         { return this.controller.identifier; }
  get element()            { return this.controller.element; }
  get validators()         { return this.controller.constructor.validators; }
  get validationElements() {
    return this.element.querySelectorAll(`[data-attr^="${this.identifier}."]`);
  }
}

class Errors {
  constructor() { this.dataMap = new Map(); }

  clear()                      { return this.dataMap.clear(); }
  has(attribute)               { return this.dataMap.has(attribute); }
  get(attribute)               { return this.dataMap.get(attribute); }
  clearAttribute(attribute)    { this.dataMap.delete(attribute); }
  forEach(...args)             { return this.dataMap.forEach(...args); }
  hasAny()                     { return !!this.size; }

  add(attribute, message) {
    const msgs = this.dataMap.get(attribute) || [];
    msgs.push(message);
    this.dataMap.set(attribute, msgs);
  }

  get size() {
    return Array.from(this.dataMap.values()).reduce((total, msgs) => total + msgs.length, 0);
  }
}

class Validator {
  constructor(controller) { this.controller = controller; }

  static get validatejs() { return Validate; }

  run(attribute) {
    this.errors.clearAttribute(attribute);
    this.validateRule(attribute);
    this.invokeValidationMethods(attribute);
  }

  validateRule(attribute) {
    const params = this.validatejsParams(attribute);
    if (!params) return;
    const messages = Validator.validatejs(...params);
    if (messages) {
      messages[attribute].forEach(message => this.errors.add(attribute, message));
    }
  }

  invokeValidationMethods(attribute) {
    const { el, value, validationMethods } = this.attributes.get(attribute);
    validationMethods.forEach(methodName => {
      const method = this.controller[methodName];
      if (!method) throw new Error(`${methodName} is not defined`);
      method.call(this.controller, { attr: attribute, value, el });
    });
  }

  validatejsParams(attribute) {
    const rule = this.rules[attribute];
    if (!rule) return null;
    const { value } = this.attributes.get(attribute);
    return [{ [attribute]: value }, { [attribute]: rule }];
  }

  get rules()      { return this.controller.constructor.rules; }
  get errors()     { return this.controller.errors; }
  get attributes() { return this.controller.attributes; }
}

export class ValidationController extends Controller {
  static rules = {};
  static validators = [];

  connect() {
    this.errors = new Errors();
    this.attributes = new Attributes(this);
    this._validator = new Validator(this);
  }

  validate(event) {
    const el = event.currentTarget;
    const attribute = attributeFromElement(el);
    if (!this.attributes.has(attribute)) this.attributes.add(attribute, el);
    const { value } = this.attributes.get(attribute);
    this.runValidator(attribute);
    this.afterValidate({ el, attr: attribute, value });
  }

  validateAll(event) {
    this.attributes.refresh();
    this.errors.clear();
    this.attributes.forEach(({ el, value }, attribute) => {
      this.runValidator(attribute);
      if (this.errors.hasAny()) event.preventDefault();
      this.afterValidate({ el, attr: attribute, value });
    });
    this.afterValidateAll(event);
  }

  runValidator(attribute) { this._validator.run(attribute); }

  // override in subclass
  afterValidate(_attribute) {}
  afterValidateAll(_event) {}

  get validatejs() { return Validator.validatejs; }
  static get validatejs() { return Validator.validatejs; }
}
